// @flow
import { useRef, useEffect, useMemo } from 'react'
import { v4 as uuid } from 'uuid'

// function to help check previous state
export function usePrevious<T>(value: ?T): ?T {
	const ref = useRef()
	useEffect(() => {
		ref.current = value
	}, [value])
	return ref.current
}

/*
 * A hook that returns a unique id
 */
export function useUniqueId(): string {
	const id = useMemo(() => {
		return uuid()
	}, [])
	return id
}

/**
 * This hook can be used in any component, displaying to the developer why the component updated.
 * Pass in the name of the component, for logging purposes, and a props object containing the data
 * you would like to monitor. It not only works with a component's props but also state objects.
 * @param {string} name
 * @param {{}} props
 */
export function useWhyDidYouUpdate(name: string, props: any) {
	// Get a mutable ref object where we can store props ...
	// ... for comparison next time this hook runs.
	const previousProps = useRef()

	useEffect(() => {
		if (previousProps.current) {
			// Get all keys from previous and current props
			const allKeys = Object.keys({ ...previousProps.current, ...props })
			// Use this object to keep track of changed props
			const changesObj = {}
			// Iterate through keys
			allKeys.forEach(key => {
				// If previous is different from current
				if (
					previousProps.current &&
					previousProps.current[key] !== props[key]
				) {
					// Add to changesObj
					changesObj[key] = {
						from: previousProps.current[key],
						to: props[key],
					}
				}
			})

			// If changesObj not empty then output to console
			if (Object.keys(changesObj).length) {
				console.log('[why-did-you-update]', name, changesObj)
			}
		}

		// Finally update previousProps with current props for next hook call
		previousProps.current = props
	})
}

/**
 * A hook that causes an event to be fired when a mouse/touch event happens outside of a react component.
 * Example:
 * const modalRef = useOnClickOutside(closeModal)
 * return <Modal ref={modalRef}>I am a modal! I will close if you click outside of me.</Modal>
 * @param {() => void} callback  The callback to be called when the event fires.
 * @return {{current: ?HTMLElement}} a ref for the element where the callback is called anytime
 * there is a mouse click outside of that component
 */
export function useOnClickOutside(callback: Node => void): {
	current: ?HTMLElement,
} {
	const node = useRef()
	const savedCallbackRef = useRef()

	useEffect(() => {
		savedCallbackRef.current = callback
	}, [callback])

	useEffect(() => {
		const handleClick = (event: TouchEvent | MouseEvent) => {
			if (
				savedCallbackRef.current &&
				event.target instanceof Node &&
				node.current &&
				// Make sure the event did not happen inside the current node
				!node.current.contains(event.target)
			) {
				savedCallbackRef.current(event.target)
			}
		}
		// add when mounted
		document.addEventListener('mousedown', handleClick)
		document.addEventListener('touchstart', handleClick)
		// return function to be called when unmounted
		return () => {
			document.removeEventListener('mousedown', handleClick)
			document.removeEventListener('touchstart', handleClick)
		}
	}, [])
	return node
}
